import { Moment } from 'moment'

import { DotLabelType } from 'Shared/molecules/DotLabels'

import { DefaultFile, SearchResult } from 'Core/interfaces'
import { StateProvince } from 'Core/utils/storeHelpers/types'

export interface DotLabelFileNames {
	dotLabel1FileName: DotLabelType | null
	dotLabel2FileName: DotLabelType | null
	dotLabel3FileName: DotLabelType | null
}

export enum WasteStreamSearchFields {
	searchById = 'searchById',
	searchByName = 'searchByName',
	searchByServiceSiteId = 'searchByServiceSiteId',
	searchByServiceSiteName = 'searchByServiceSiteName',
	searchByRefCode = 'searchByRefCode'
}

export enum WasteStreamFilterFields {
	includeInactive = 'includeInactive'
}

type HazardCodeSearchSuggestion = Entity & { code: string }

export declare namespace WasteStream {
	interface Basic extends Entity, DotLabelFileNames {
		serviceSite: Entity
		dotShipping: Entity | null
		epaForm: Entity | null
		epaSource: Entity | null
		managementMethod: Entity | null
		dotPacking: Entity | null
		isActive: boolean
		refCode: string | null
		technicalDescription: string | null
		isReportableQuantity: boolean
		specialHandlingDescription: string | null
		comments: string | null
		ergNumber: string | null
		lastEvaluationDate: string | null
		reEvaluationDate: string | null
		supplierLocation: string | null
		companyCategory: {
			code: string
			name: string
		} | null
		profileFilename: string | null
		wasteCatalogue: CodeEntity | null
		category: CodeDescription | null
		hazardWasteLimits: CodeDescription[] | null
		disposalRecovery: CodeDescription | null
		hazardProperties: CodeEntity[] | null
		generatingProcessDescription: BasicString | null
		isTurkish: boolean
	}
	namespace DotShipping {
		interface Item extends Entity {
			unnaNumber: string
		}
		interface Search {
			searchSuggestionByName: Item[]
			searchSuggestionByUnnaNumber: Item[]
		}
	}

	namespace WasteCatalogue {
		interface Search {
			searchByCode: CodeEntity[]
			searchByName: CodeEntity[]
		}
	}

	type ListItem = Pick<Basic, 'id' | 'name' | 'refCode' | 'isActive'> & {
		serviceSite: Entity
	}

	namespace StateHazCode {
		interface Basic {
			id: Entity['id']
			displayOrder: number
			stateHazCode: HazardCodeSearchSuggestion
		}

		interface BasicWithHes {
			id: BasicNumber
			uniqId: number
			current: StateHazCode.Basic | null
			new: HesWastestream.Basic['stateHazCodes'][number] | null
		}

		interface Form {
			displayOrder: number | null
			stateHazCode: LabelValue<number> | null
		}

		interface Request {
			displayOrder: number | null
			stateHazCodeId: number | null
		}

		namespace Search {
			interface Result extends HazardCodeSearchSuggestion {}
		}
	}

	namespace EpaHazardCode {
		type Basic = {
			id: Entity['id']
			displayOrder: number
			epaHazCode: string
			epaHazName: string
			epaHazCodeId: number
			subCategoryCode: BasicString
			epaHazCodeSubCategoryId: string | null
			epaHazCodeSubCategories: { id: number; code: string }[]
		}

		interface BasicWithHes {
			id: BasicNumber
			uniqId: number
			current: EpaHazardCode.Basic | null
			new: HesWastestream.Basic['epaHazCodes'][number] | null
		}

		interface Form {
			displayOrder: number | null
			epaHazCode: {
				label: string
				value: number
			} | null
			epaHazCodeSubCategoryId: string | null
		}

		interface Request {
			displayOrder: number | null
			epaHazCodeId: number | null
			epaHazCodeSubCategoryId: string | null
		}

		namespace Search {
			interface Result extends HazardCodeSearchSuggestion {}
		}
	}

	namespace PhysicalCharacteristic {
		interface Basic {
			id: Entity['id']
			description: string | null
			physicalCharacteristic: string | null
			physicalCharacteristicId: number
		}

		interface BasicWithHes {
			id: BasicNumber
			uniqId: number
			current: PhysicalCharacteristic.Basic | null
			new: HesWastestream.Basic['physicalCharacteristics'][number] | null
		}

		interface Form {
			physicalCharacteristicId: number | null
			description: string | null
		}

		interface Request extends Form {}
	}

	namespace Constituent {
		interface Basic {
			id: Entity['id']
			name: string
			description: string | null
			unitOfMeasure: Entity | null
			concentrationAmount: string | null
			resultQualifier: string | null
		}
		interface Request extends Form {}
		interface Form {
			name: string | null
			description: string | null
			concentrationAmount: string | null
			resultQualifier: string | null
			unitOfMeasureId: number | null
		}
	}

	namespace DuplicateWasteStream {
		interface Form {
			name: string | null
			serviceSite: LabelValue<number> | null
			refCode: string | null
			isActive: boolean | null
		}

		interface Request extends Pick<Form, 'name' | 'refCode' | 'isActive'> {
			serviceSiteId: number
		}
	}

	namespace HesWastestream {
		interface Basic {
			id: number
			serviceSite: Entity
			supplierLocation: BasicString
			name: BasicString
			refCode: BasicString
			companyCategory: NameCode
			isActive: boolean
			profileFilename: BasicString
			dotShipping: Entity | null
			dotPacking: Entity | null
			dotLabel1FileName: BasicString
			dotLabel2FileName: BasicString
			dotLabel3FileName: BasicString
			technicalDescription: BasicString
			isReportableQuantity: boolean
			epaForm: Entity | null
			epaSource: Entity | null
			ergNumber: BasicString
			managementMethod: Entity | null
			specialHandlingDescription: BasicString
			comments: BasicString
			lastEvaluationDate: BasicString
			reEvaluationDate: BasicString
			generatingProcessDescription: BasicString
			category: CodeDescription | null
			disposalRecovery: CodeDescription | null
			wasteCatalogue: CodeEntity | null
			hazardWasteLimits: CodeDescription[]
			hazardProperties: CodeEntity[]
			epaHazCodes: {
				id: number
				displayOrder: BasicNumber
				epaHazCode: BasicString
				epaHazName: BasicString
				epaHazCodeId: BasicNumber
				epaHazCodeSubCategoryId: BasicNumber
				subCategoryCode: BasicString
				epaHazCodeSubCategories: CodeEntity[]
			}[]
			stateHazCodes: {
				id: number
				displayOrder: BasicNumber
				stateHazCode: CodeEntity & {
					stateProvince: StateProvince
				}
			}[]
			physicalCharacteristics: {
				id: number
				description: BasicString
				physicalCharacteristic: BasicString
				physicalCharacteristicId: BasicNumber
			}[]
			isTurkish: boolean
		}

		namespace Form {
			interface AddFromHes {
				serviceSiteId: BasicNumber
				refCode: BasicString
			}
		}
	}

	namespace Form {
		interface Add {
			name: string
			serviceSiteId: LabelValue<number> | null
			refCode: string
			isActive: boolean
		}

		interface Edit
			extends Pick<
				Basic,
				| 'name'
				| 'refCode'
				| 'isActive'
				| 'isReportableQuantity'
				| 'technicalDescription'
				| 'specialHandlingDescription'
				| 'comments'
				| 'ergNumber'
			> {
			serviceSite: LabelValue<number>
			dotShipping: LabelValue<number> | null
			epaFormId: Entity['id'] | null
			epaSourceId: Entity['id'] | null
			dotPackingId: Entity['id'] | null
			managementMethodId: Entity['id'] | null
			lastEvaluationDate: Moment | null
			reEvaluationDate: Moment | null
			profile: DefaultFile | { file: null; fileList: [] } | null
			wasteCatalogueId: LabelValue<number> | null
			wasteCategoryId: Key | null
			disposalRecoveryId: Key | null
			hazardPropertyIds: LabelValue<number>[]
			hazardWasteLimitIds: LabelValue<number>[]
			generatingProcessDescription: BasicString
		}
	}

	namespace Request {
		interface Add extends Pick<Form.Add, 'name' | 'refCode' | 'isActive'> {
			serviceSiteId: number
		}
		interface Edit
			extends Pick<
				Basic,
				| 'name'
				| 'refCode'
				| 'isActive'
				| 'isReportableQuantity'
				| 'technicalDescription'
				| 'specialHandlingDescription'
				| 'comments'
				| 'ergNumber'
				| 'lastEvaluationDate'
				| 'reEvaluationDate'
			> {
			dotShippingId: number | null
			serviceSiteId: number
			technicalDescription: string | null
			epaFormId: Entity['id'] | null
			epaSourceId: Entity['id'] | null
			dotPackingId: Entity['id'] | null
			managementMethodId: Entity['id'] | null
			'Profile.Value': null | File
			'Profile.HasVaLue': boolean
			wasteCatalogueId: Key | null
			hazardPropertyIds: Key[] | null
			hazardWasteLimitIds: Key[] | null
			wasteCategoryId: Key | null
			disposalRecoveryId: Key | null
			generatingProcessDescription: BasicString
		}
	}

	namespace Search {
		interface Result extends SearchResult {
			[WasteStreamSearchFields.searchByServiceSiteId]: Entity[]
			[WasteStreamSearchFields.searchByServiceSiteName]: Entity[]
			[WasteStreamSearchFields.searchByRefCode]: Entity[]
		}
	}
}
